// client/src/coms/conversations/QuickSwitcher.js
// Quick conversation switcher: from any channel or DM (the ⇄ button in the top bar on phones, Ctrl/⌘+K on a
// keyboard). Recent, pinned and unread conversations at once; type to search every person, server and channel.
// Arrow keys move, Enter opens, Esc closes.
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import ConversationSections from './ConversationList';
import './conversations.css';

export default function QuickSwitcher({ index, onOpen, onClose, activeId, pinnedIds }) {
  const [query, setQuery] = useState('');
  const [cursor, setCursor] = useState(0);
  const inputRef = useRef(null);
  const prevFocus = useRef(typeof document !== 'undefined' ? document.activeElement : null);

  useEffect(() => {
    const t = setTimeout(() => inputRef.current && inputRef.current.focus(), 30);
    const restore = prevFocus.current;
    // Esc closes even before focus has reached the search box.
    const onKey = (e) => {
      if (e.key === 'Escape' && !(inputRef.current && inputRef.current.contains(document.activeElement))) {
        e.preventDefault();
        onClose();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('keydown', onKey);
      clearTimeout(t);
      if (restore && restore.isConnected && restore.focus) restore.focus();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const sections = useMemo(() => {
    if (query.trim()) {
      const r = index.search(query);
      return [{ key: 'results', title: r.length ? 'Results' : 'No results', items: r, empty: `Nothing matches “${query.trim()}”.` }];
    }
    return [
      { key: 'unread', title: 'Unread', items: index.unread.slice(0, 8) },
      { key: 'pinned', title: 'Pinned', items: index.pinned },
      { key: 'recent', title: 'Recent', items: index.recent.filter((it) => it.id !== activeId).slice(0, 12) },
    ];
  }, [index, query, activeId]);
  const flat = useMemo(() => sections.flatMap((s) => s.items), [sections]);
  const focused = flat[Math.min(cursor, flat.length - 1)];

  useEffect(() => setCursor(0), [query]);
  useEffect(() => {
    if (!focused) return;
    const el = document.getElementById(`vx-qs-${focused.id}`);
    if (el && el.scrollIntoView) el.scrollIntoView({ block: 'nearest' });
  }, [focused]);

  const onKeyDown = (e) => {
    if (e.key === 'Escape') {
      e.preventDefault();
      onClose();
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      setCursor((c) => Math.min(flat.length - 1, c + 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setCursor((c) => Math.max(0, c - 1));
    } else if (e.key === 'Enter' && focused) {
      e.preventDefault();
      onOpen(focused);
    }
  };

  return createPortal(
    <div className="vx-qs-backdrop vx-pz" onMouseDown={(e) => e.target === e.currentTarget && onClose()} data-testid="quick-switcher">
      <div className="vx-qs" role="dialog" aria-modal="true" aria-label="Switch conversation" onKeyDown={onKeyDown}>
        <div className="vx-qs-head">
          <input
            ref={inputRef}
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Jump to a person, server or channel"
            aria-label="Search conversations"
            aria-activedescendant={focused ? `vx-qs-${focused.id}` : undefined}
            enterKeyHint="go"
            data-testid="quick-switcher-input"
          />
          <button type="button" className="vx-btn small" onClick={onClose}>
            Close
          </button>
        </div>
        <div className="vx-qs-body">
          {flat.length || query.trim() ? (
            <ConversationSections sections={sections} onOpen={onOpen} pinnedIds={pinnedIds} activeId={activeId} focusedId={focused && focused.id} idPrefix="vx-qs" />
          ) : (
            <p className="vx-conv-empty">{index.loaded ? 'No other conversations yet. Type to search every channel and DM.' : 'Loading…'}</p>
          )}
        </div>
        <div className="vx-qs-foot vx-set-hint">↑ ↓ to move · Enter to open · Esc to close</div>
      </div>
    </div>,
    document.body
  );
}
